import { useState } from 'react';
import { Button, DatePicker, Descriptions, Input, Table, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useFinanceTable from '../../../hooks/useFinanceTable';
import { authDetail, authExport, authList } from '../../../api/finance';
import FinanceExportButton from '../../../components/finance/FinanceExportButton';
import FinanceDetailModal from '../../../components/finance/FinanceDetailModal';
import FinanceStatusTag from '../../../components/finance/FinanceStatusTag';
import { can } from '../../../util/permission';
import { dayEnd, dayStart, formatDateTime } from '../../../util/finance';

function prettyJson(value) {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
}

export default function AuthTab() {
  const table = useFinanceTable(authList);
  const [form, setForm] = useState({ range: null, realName: '', userId: '' });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const search = () => {
    table.search({
      startTime: form.range ? dayStart(form.range[0]) : undefined,
      endTime: form.range ? dayEnd(form.range[1]) : undefined,
      realName: form.realName || undefined,
      userId: form.userId || undefined,
    });
  };

  const reset = () => {
    setForm({ range: null, realName: '', userId: '' });
    table.reset();
  };

  const openDetail = async (record) => {
    try {
      const res = await authDetail(record.id);
      setDetail(res);
      setDetailOpen(true);
    } catch (error) {
      message.error(error?.message || '查询存证失败');
    }
  };

  const columns = [
    { title: '用户ID', dataIndex: 'userId', width: 100, fixed: 'left' },
    { title: '姓名', dataIndex: 'realName', width: 110 },
    { title: '身份证号', dataIndex: 'idCardNo', width: 180 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '签署时间', dataIndex: 'signTime', width: 170, render: formatDateTime },
    { title: '授权书版本', dataIndex: 'docVersionNo', width: 130 },
    { title: '验证方式', dataIndex: 'verifyMethod', width: 120 },
    { title: '证据快照哈希', dataIndex: 'evidenceHash', width: 280, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'statusText',
      width: 90,
      render: (text) => <FinanceStatusTag text={text} />,
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) =>
        can('finance:auth:detail') ? (
          <Button type="link" onClick={() => openDetail(record)}>
            查看存证
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="finance-toolbar">
        <DatePicker.RangePicker
          showTime
          value={form.range}
          onChange={(value) => setForm({ ...form, range: value })}
          style={{ width: 360 }}
        />
        <Input
          value={form.realName}
          onChange={(e) => setForm({ ...form, realName: e.target.value })}
          placeholder="姓名"
          style={{ width: 140 }}
          allowClear
        />
        <Input
          value={form.userId}
          onChange={(e) => setForm({ ...form, userId: e.target.value.replace(/\D/g, '') })}
          placeholder="用户ID"
          style={{ width: 120 }}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={search}>
          查询
        </Button>
        <Button onClick={reset}>重置</Button>
        {can('finance:auth:export') && (
          <FinanceExportButton exportApi={authExport} query={table.query} />
        )}
      </div>
      <Table
        className="finance-table"
        columns={columns}
        dataSource={table.data}
        rowKey="id"
        loading={table.loading}
        scroll={{ x: 1500 }}
        rowClassName={(_, index) => (index % 2 === 1 ? 'finance-row-odd' : '')}
        pagination={{
          current: table.pagination.current,
          pageSize: table.pagination.pageSize,
          total: table.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: table.onTableChange,
        }}
      />
      <FinanceDetailModal
        open={detailOpen}
        title="授权存证详情"
        width={960}
        onClose={() => setDetailOpen(false)}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="用户ID">{detail.userId}</Descriptions.Item>
              <Descriptions.Item label="姓名">{detail.realName || '-'}</Descriptions.Item>
              <Descriptions.Item label="身份证号">{detail.idCardNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{detail.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="授权书版本">{detail.docVersionNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="签署时间">{formatDateTime(detail.signTime)}</Descriptions.Item>
              <Descriptions.Item label="验证方式">{detail.verifyMethod}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <FinanceStatusTag text={detail.statusText} />
              </Descriptions.Item>
              <Descriptions.Item label="二要素核验流水号" span={2}>
                {detail.twoFactorVerifyId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="短信发送时间">{formatDateTime(detail.smsSendTime)}</Descriptions.Item>
              <Descriptions.Item label="短信校验时间">{formatDateTime(detail.smsVerifyTime)}</Descriptions.Item>
              <Descriptions.Item label="客户端IP">{detail.clientIp || '-'}</Descriptions.Item>
              <Descriptions.Item label="User-Agent">{detail.userAgent || '-'}</Descriptions.Item>
              <Descriptions.Item label="文书哈希">{detail.docContentHash || '-'}</Descriptions.Item>
              <Descriptions.Item label="证据快照哈希">{detail.evidenceHash || '-'}</Descriptions.Item>
            </Descriptions>
            <h4 className="finance-section-title">授权书全文</h4>
            <pre className="finance-pre">{detail.docContent || '授权书正文为空'}</pre>
            <h4 className="finance-section-title">证据包</h4>
            <pre className="finance-pre">{prettyJson(detail.evidenceSnapshot) || '证据包为空'}</pre>
          </>
        )}
      </FinanceDetailModal>
    </div>
  );
}
